(function () {
  'use strict';
  if (!window.FleetCartCore || window.FleetCart) return;
  const core = window.FleetCartCore;
  const strings = window.fleetStrings || {};
  const message = (failure) => strings[failure?.code] || failure?.message || strings.error;
  const queue = core.createQueue((pending) => {
    document.documentElement.classList.toggle('fleet-cart-busy', pending > 0);
    document.dispatchEvent(new CustomEvent('fleet:cart-busy', { detail: { pending } }));
  });
  const endpoint = (kind) => {
    const route = kind === 'get' ? routes.cart_url : routes['cart_' + kind + '_url'];
    return route.replace(/\.(js|json)$/, '') + '.js';
  };
  const sectionIds = () => [
    'cart-drawer', 'cart-icon-bubble',
    document.getElementById('main-cart-items')?.dataset.id,
    document.getElementById('main-cart-footer')?.dataset.id
  ].filter(Boolean);
  const rawRequest = async (kind, payload) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const options = { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json' } };
      if (payload) {
        options.method = 'POST';
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify({ ...payload, sections: sectionIds(), sections_url: window.location.pathname });
      }
      return await core.readJSON(await fetch(endpoint(kind), options));
    } catch (failure) {
      if (failure.name === 'AbortError' || failure instanceof TypeError) {
        throw Object.assign(new Error(strings.unconfirmed), { code: 'unconfirmed' });
      }
      throw failure;
    } finally { clearTimeout(timeout); }
  };
  const notify = (cart) => {
    if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    document.dispatchEvent(new CustomEvent('fleet:cart-updated', { detail: { cart } }));
  };
  const render = async (cart, open = true) => {
    let sections = cart.sections;
    if (!sections || sectionIds().some((id) => typeof sections[id] !== 'string')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds().join(','));
      sections = await core.readJSON(await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(20000) }));
    }
    if (sectionIds().some((id) => typeof sections[id] !== 'string')) {
      throw Object.assign(new Error(strings.refresh), { code: 'refresh' });
    }
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      if (Number.isInteger(cart.item_count)) drawer.classList.toggle('is-empty', cart.item_count === 0);
      drawer.renderContents({ ...cart, sections }, { open });
    }
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const target = document.getElementById(id);
      const html = target && sections[target.dataset.id];
      if (!html) continue;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-contents');
      const destination = target.querySelector('.js-contents');
      if (source && destination) destination.innerHTML = source.innerHTML;
      target.classList.toggle('is-empty', cart.item_count === 0);
    }
    notify(cart);
  };
  const standardUpdate = (element, action, lines) => {
    const EventType = window.StandardEvents?.CartLinesUpdateEvent;
    if (!EventType) return null;
    const deferred = EventType.createPromise();
    element.dispatchEvent(new EventType({ action, context: 'cart', lines, promise: deferred.promise }));
    return {
      resolve: (cart) => deferred.resolve({ cart: EventType.createCartFromAjaxResponse(cart) }),
      reject: (failure) => deferred.reject(failure)
    };
  };
  const cartStatusTarget = () => {
    const drawer = document.querySelector('cart-drawer');
    const drawerStatus = document.querySelector('[data-fleet-cart-status]');
    const pageStatus = document.getElementById('cart-errors');
    return drawer?.classList.contains('active') ? drawerStatus || pageStatus : pageStatus || drawerStatus;
  };
  const report = (failure) => {
    const target = cartStatusTarget();
    if (target) {
      target.textContent = message(failure);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fleet-cart__review link underlined-link';
      button.dataset.fleetCartReview = '';
      button.textContent = strings.review_cart;
      target.append(button);
      target.focus();
    }
  };
  window.FleetCart = {
    queue, request: rawRequest, render, report,
    async fetchRaw(url, options) {
      const response = await fetch(url, { ...options, signal: AbortSignal.timeout(20000) });
      await core.readJSON(response.clone());
      return response;
    },
    fetch(url, options) {
      return queue.run(() => this.fetchRaw(url, options));
    },
    async refresh(open = false) { const cart = await rawRequest('get'); await render(cart, open); return cart; }
  };

  document.addEventListener('submit', async (event) => {
    if (event.submitter?.name !== 'checkout' || !event.target.matches('form[action*="/cart"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.submitter;
    if (button.dataset.fleetChecking === 'true') return;
    button.dataset.fleetChecking = 'true';
    button.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(routes.cart_url, window.location.origin);
      url.pathname = url.pathname.replace(/\/cart\/?$/, '/checkout');
      url.search = '';
      while (true) {
        await queue.idle();
        const revision = queue.revision;
        const cart = await rawRequest('get');
        if (!queue.isCurrent(revision)) continue;
        if (cart.items.length) {
          window.location.assign(url.href);
          return;
        }
        await render(cart);
        return;
      }
    } catch (failure) {
      report(failure);
    } finally {
      delete button.dataset.fleetChecking;
      button.removeAttribute('aria-busy');
    }
  }, true);

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-fleet-cart-review]');
    if (!button || button.disabled) return;
    event.preventDefault();
    button.disabled = true;
    try {
      while (true) {
        await queue.settled();
        const revision = queue.revision;
        const cart = await rawRequest('get');
        if (queue.pending !== 0 || queue.revision !== revision) continue;
        await render(cart, false);
        if (!queue.acknowledge(revision)) continue;
        const status = cartStatusTarget();
        if (status) {
          status.textContent = strings.reviewed;
          status.focus();
        }
        return;
      }
    } catch (failure) {
      report(failure);
    } finally {
      button.disabled = false;
    }
  });

  class FleetPackPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      try { this.variants = JSON.parse(this.querySelector('[data-pack-variants]').textContent); } catch { return; }
      this.input = this.form.querySelector('[name="id"]');
      this.quantityInput = this.form.elements.namedItem('quantity');
      if (!this.input) return;
      this.initialized = true;
      this.variantId = this.input.value;
      this.choiceIndex = 0;
      this.customQuantity = false;
      this.slots = [];
      this.canMix = this.dataset.mix === 'true' && this.variants.length > 1 &&
        this.variants.every((variant) => variant.min === 1 && variant.increment === 1);
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-pack-choice]')) {
          this.choiceIndex = Number(event.target.dataset.packChoice);
          this.customQuantity = false;
          this.slots = [];
          this.update();
        } else if (event.target.matches('[data-pack-slot]')) {
          this.slots[Number(event.target.dataset.packSlot)] = event.target.value;
          this.updateTotal();
        }
      });
      this.onVariantChange = () => {
        if (this.variantId === this.input.value) return;
        this.variantId = this.input.value;
        this.choiceIndex = 0;
        this.slots = [];
        this.update();
      };
      this.input.addEventListener('change', this.onVariantChange);
      this.querySelector('[data-pack-custom]').addEventListener('click', () => {
        this.customQuantity = !this.customQuantity;
        this.slots = [];
        this.update();
        if (this.customQuantity) this.quantityInput?.focus();
      });
      if (this.quantityInput) {
        this.onQuantityChange = () => this.updateTotal();
        this.quantityInput.addEventListener('change', this.onQuantityChange);
      }
      this.hidden = false;
      this.update();
    }
    disconnectedCallback() {
      this.input?.removeEventListener('change', this.onVariantChange);
      this.quantityInput?.removeEventListener('change', this.onQuantityChange);
      if (this.quantityWrapper) this.quantityWrapper.hidden = false;
    }
    formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    }
    update() {
      const variant = this.variants.find((entry) => String(entry.id) === String(this.variantId));
      this.quantities = core.quantities(variant);
      if (this.choiceIndex >= this.quantities.length) this.choiceIndex = 0;
      this.quantity = this.quantities[this.choiceIndex] || 1;
      const choices = this.querySelector('[data-pack-choices]');
      choices.replaceChildren();
      this.quantities.forEach((quantity, index) => {
        const label = document.createElement('label');
        label.className = 'fleet-pack__choice';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'fleet-pack-' + this.dataset.sectionId;
        input.dataset.packChoice = String(index);
        input.checked = index === this.choiceIndex && !this.customQuantity;
        const text = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = strings.quantity.replace('[quantity]', quantity);
        const price = document.createElement('small');
        price.textContent = this.formatMoney(variant.price * quantity);
        text.append(title, price);
        label.append(input, text);
        choices.append(label);
      });
      this.quantityWrapper = this.quantityInput?.closest('.product-form__quantity');
      if (this.quantityWrapper) this.quantityWrapper.hidden = !this.customQuantity;
      if (!this.customQuantity && this.quantityInput) this.quantityInput.value = this.quantity;
      this.querySelector('[data-pack-custom]').hidden = !this.quantityInput;
      this.querySelector('[data-pack-custom]').textContent = this.customQuantity ? strings.back_to_set : strings.custom;
      const slotContainer = this.querySelector('[data-pack-slots]');
      slotContainer.replaceChildren();
      if (this.canMix && this.quantity > 1 && this.quantity <= 3 && !this.customQuantity) {
        for (let index = 0; index < this.quantity; index++) {
          const wrapper = document.createElement('div');
          wrapper.className = 'fleet-pack__slot';
          const label = document.createElement('label');
          const id = 'FleetPackSlot-' + this.dataset.sectionId + '-' + index;
          label.htmlFor = id;
          label.textContent = strings.item.replace('[number]', index + 1);
          const select = document.createElement('select');
          select.id = id;
          select.className = 'fleet-input';
          select.dataset.packSlot = String(index);
          for (const entry of this.variants) {
            const option = document.createElement('option');
            option.value = String(entry.id);
            option.disabled = !entry.available;
            option.textContent = entry.title + ' — ' + this.formatMoney(entry.price) + (entry.available ? '' : ' · ' + strings.sold_out);
            select.append(option);
          }
          this.slots[index] = this.slots[index] || this.variantId;
          select.value = String(this.slots[index]);
          wrapper.append(label, select);
          slotContainer.append(wrapper);
        }
      }
      this.updateTotal();
      const payment = this.form.querySelector('.shopify-payment-button');
      if (payment) payment.hidden = !this.customQuantity && this.quantity > 1;
    }
    getItems() {
      if (this.hidden || !this.initialized) return null;
      if (this.customQuantity) return core.buildItems(this.variants, [{ id: this.input.value, quantity: this.quantityInput.value }]);
      if (!this.quantities.length) throw Object.assign(new Error(strings.sold_out), { code: 'sold_out' });
      const selections = this.canMix && this.quantity > 1 && this.quantity <= 3 ?
        this.slots.slice(0, this.quantity).map((id) => ({ id, quantity: 1 })) :
        [{ id: this.input.value, quantity: this.quantity }];
      return core.buildItems(this.variants, selections);
    }
    updateTotal() {
      const total = this.querySelector('[data-pack-total]');
      try {
        const items = this.getItems();
        const cents = items.reduce((sum, item) => sum + this.variants.find((entry) => String(entry.id) === String(item.id)).price * item.quantity, 0);
        total.textContent = this.formatMoney(cents);
      } catch { total.textContent = strings.unavailable; }
    }
    applyToFormData(data) {
      const items = this.getItems();
      if (!items) return null;
      const properties = [...data.entries()].filter(([key]) => key.startsWith('properties['));
      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) return null;
      data.delete('id');
      data.delete('quantity');
      for (const [key] of properties) data.delete(key);
      items.forEach((item, index) => {
        data.append('items[' + index + '][id]', item.id);
        data.append('items[' + index + '][quantity]', item.quantity);
        for (const [key, value] of properties) data.append('items[' + index + '][' + key.replace('[', ']['), value);
      });
      return items;
    }
  }

  class FleetCartDiscount extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.hidden = false;
      this.codes = [];
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-discount-status]');
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        const code = this.form.elements.discount.value.trim();
        if (code) this.apply(code);
      });
      this.onCartUpdated = (event) => this.showCodes(event.detail.cart);
      document.addEventListener('fleet:cart-updated', this.onCartUpdated);
      rawRequest('get').then((cart) => { if (this.isConnected) this.showCodes(cart); }).catch(() => {});
    }
    disconnectedCallback() { document.removeEventListener('fleet:cart-updated', this.onCartUpdated); }
    showCodes(cart) {
      this.codes = core.requestedDiscountCodes(cart);
      const confirmed = new Set(core.discountCodes(cart).map((code) => code.toLowerCase()));
      const list = this.querySelector('[data-discount-codes]');
      list.replaceChildren();
      for (const code of this.codes) {
        const item = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = confirmed.has(code.toLowerCase()) ? code : code + ' · ' + strings.code_pending;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fleet-discount__remove';
        button.textContent = '×';
        button.setAttribute('aria-label', strings.remove_code.replace('[code]', code));
        button.addEventListener('click', () => this.apply(null, code));
        item.append(label, button);
        list.append(item);
      }
    }
    async apply(code, remove) {
      const field = this.form.elements.discount;
      const button = this.form.querySelector('button');
      button.disabled = true;
      this.status.textContent = strings.updating;
      try {
        await queue.run(async () => {
          const before = await rawRequest('get');
          const codes = core.requestedDiscountCodes(before).filter((entry) => entry.toLowerCase() !== remove?.toLowerCase());
          if (code && !codes.some((entry) => entry.toLowerCase() === code.toLowerCase())) codes.push(code);
          const cart = await rawRequest('update', { discount: codes.join(',') });
          await render(cart, false);
          const applied = !code || core.discountCodes(cart).some((entry) => entry.toLowerCase() === code.toLowerCase());
          const current = document.querySelector('fleet-cart-discount');
          if (current) {
            current.querySelector('details').open = true;
            current.querySelector('[data-discount-status]').textContent = remove ? strings.code_removed : applied ? strings.code_applied : strings.code_invalid;
          }
        });
      } catch (failure) {
        const current = document.querySelector('fleet-cart-discount') || this;
        current.querySelector('[data-discount-status]').textContent = message(failure);
      } finally { button.disabled = false; if (field.isConnected) field.value = ''; }
    }
  }

  class FleetCartVariant extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.hidden = false;
      this.select = this.querySelector('select');
      this.button = this.querySelector('[data-variant-save]');
      this.select.addEventListener('change', (event) => {
        event.stopPropagation();
        this.button.hidden = this.select.value === this.dataset.variantId;
      });
      this.button.addEventListener('click', () => this.swap());
    }
    async swap() {
      const key = this.dataset.lineKey;
      const variantId = this.select.value;
      if (!variantId || variantId === this.dataset.variantId) return;
      this.button.disabled = true;
      this.select.disabled = true;
      this.querySelector('[data-variant-status]').textContent = strings.updating;
      try {
        await queue.run(async () => {
          const cart = await rawRequest('get');
          const updated = await core.replaceVariant(rawRequest, cart, key, variantId);
          await render(updated, false);
        });
      } catch (failure) {
        try { await window.FleetCart.refresh(false); } catch {}
        report(failure);
      } finally {
        this.button.disabled = false;
        this.select.disabled = false;
      }
    }
  }

  class FleetCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.select = this.querySelector('select');
      this.button = this.querySelector('[data-upsell-add]');
      this.querySelector('[data-upsell-controls]').hidden = false;
      this.select.addEventListener('change', (event) => {
        event.stopPropagation();
        this.querySelector('[data-upsell-price]').textContent = this.select.selectedOptions[0].dataset.price;
        this.querySelector('[data-upsell-quantity]').textContent = strings.quantity.replace('[quantity]', this.select.selectedOptions[0].dataset.quantity);
      });
      this.button.addEventListener('click', () => this.add());
    }
    async add() {
      const option = this.select.selectedOptions[0];
      if (!option || option.disabled) return;
      this.button.disabled = true;
      this.querySelector('[data-upsell-status]').textContent = strings.updating;
      const event = standardUpdate(this, 'add', [{ merchandiseId: option.value, quantity: Number(option.dataset.quantity) || 1 }]);
      try {
        await queue.run(async () => {
          const quantity = Number(option.dataset.quantity) || 1;
          const added = await rawRequest('add', { items: [{ id: option.value, quantity }] });
          const cart = await rawRequest('get');
          event?.resolve(cart);
          await render({ ...cart, sections: added.sections }, false);
        });
      } catch (failure) {
        event?.reject(failure);
        this.querySelector('[data-upsell-status]').textContent = message(failure);
      } finally { this.button.disabled = false; }
    }
  }

  for (const [name, element] of [
    ['fleet-pack-picker', FleetPackPicker], ['fleet-cart-discount', FleetCartDiscount],
    ['fleet-cart-variant', FleetCartVariant], ['fleet-cart-upsell', FleetCartUpsell]
  ]) if (!customElements.get(name)) customElements.define(name, element);
})();
